import { NSARouterMakerResult } from "namirasoft-account-react";
import { ConditionGroupFullRow, ConditionGroupInputRow, ConditionGroupLineInputRow, ConditionGroupRow, ConditionGroupOperator } from "namirasoft-inference";
import { NSBox, NSBoxBoolean, NSBoxEnum, NSBoxString, NSBoxTextArea, NSColumn, NSRepeater, NSRow } from "namirasoft-site-react";
import { Component, createRef } from "react";
import ConditionGroupLineComponent from "../components/ConditionGroupLineComponent";
import { InferenceEntities } from "../InferenceEntities";
import { InferenceInfo } from "../InferenceInfo";
import { InferenceLayout } from '../InferenceLayout';
import { InferenceRouterProps } from "../InferenceRouterProps";

interface ConditionGroupEditPageProps extends NSARouterMakerResult, InferenceRouterProps
{
    isEdit: boolean;
}

export default class ConditionGroupEditPage extends Component<ConditionGroupEditPageProps>
{
    NSRepeater_Lines = createRef<NSRepeater<ConditionGroupLineComponent, ConditionGroupLineInputRow>>();
    NSBoxString_Name = createRef<NSBoxString>();
    NSBoxBoolean_Not = createRef<NSBoxBoolean>();
    NSBoxEnum_Operator = createRef<NSBoxEnum<ConditionGroupOperator>>();
    NSBoxString_LogGroupId = createRef<NSBoxString>();
    NSBoxTextArea_Description = createRef<NSBoxTextArea>();

    override render()
    {
        return (
            <InferenceLayout<ConditionGroupRow, ConditionGroupInputRow>
                {...this.props}
                action={{ description: this.props.isEdit ? InferenceInfo.condition_group.edit : InferenceInfo.condition_group.create }}
                pages={{
                    entity: InferenceEntities.condition_group(this.props),
                    edit: {
                        ...this.props,
                        ui: {
                            tab_page_edit: {
                                editor: {
                                    getInputRow: (): ConditionGroupInputRow =>
                                    {
                                        let name = this.NSBoxString_Name.current?.getValue() ?? "";
                                        let not = this.NSBoxBoolean_Not.current?.getValue() ?? false;
                                        let operator = this.NSBoxEnum_Operator.current?.getValueEnumOne(ConditionGroupOperator, ConditionGroupOperator.AND) ?? ConditionGroupOperator.AND;
                                        let log_group_id = this.NSBoxString_LogGroupId.current?.getValue() ?? null;
                                        let description = this.NSBoxTextArea_Description.current?.getValue() ?? null;
                                        let lines = this.NSRepeater_Lines.current?.getValue() ?? [];

                                        return {
                                            workspace_id: null,
                                            name,
                                            not,
                                            operator,
                                            log_group_id,
                                            description,
                                            lines,
                                            condition_group_category: [],
                                            condition_group_field: [],
                                            condition_group_tag: [],
                                        };
                                    },
                                    setRow: (row: ConditionGroupRow) =>
                                    {
                                        let full = row as ConditionGroupFullRow;

                                        this.NSBoxString_Name.current?.setValue(full.name);
                                        this.NSBoxBoolean_Not.current?.setValue(full.not);
                                        this.NSBoxEnum_Operator.current?.setValue(full.operator);
                                        this.NSBoxString_LogGroupId.current?.setValue(full.log_group_id);
                                        this.NSBoxTextArea_Description.current?.setValue(full.description);
                                        this.NSRepeater_Lines.current?.setValue((full.lines ?? []).map(l => ({
                                            ccg_type: l.ccg_type,
                                            ccg_id: l.ccg_id,
                                            second_operand_type: l.second_operand_type,
                                            second_ccg_type: l.second_ccg_type,
                                            second_ccg_id: l.second_ccg_id,
                                        })));
                                    }
                                },
                                ui: {
                                    extra_tabs: [
                                        {
                                            title: "Lines",
                                            clicked: true,
                                            getContent: () => (
                                                <NSRepeater<ConditionGroupLineComponent, ConditionGroupLineInputRow>
                                                    ref={this.NSRepeater_Lines}
                                                    title="Lines"
                                                    required
                                                    min={1}
                                                    createItem={(gIndex: number, lIndex: number, ref: React.RefObject<ConditionGroupLineComponent>, onChange: () => void, onDelete: undefined | ((index: number) => void)) =>
                                                    {
                                                        return <ConditionGroupLineComponent
                                                            ref={ref}
                                                            {...this.props}
                                                            key={`ConditionGroupLine_${gIndex}`}
                                                            index={lIndex}
                                                            deletable={true}
                                                            onChanged={() => { onChange(); }}
                                                            onDelete={() => { onDelete?.(lIndex); }}
                                                        />;
                                                    }}
                                                    isEmpty={function (_, value: ConditionGroupLineInputRow | null): boolean
                                                    {
                                                        return !ConditionGroupLineComponent.IS_DIRTY(value);
                                                    }}
                                                    getValue={function (item: ConditionGroupLineComponent, checkError: boolean): ConditionGroupLineInputRow | null
                                                    {
                                                        return item.getValue(checkError);
                                                    }}
                                                    setValue={function (item: ConditionGroupLineComponent, _: number, value: ConditionGroupLineInputRow | null): void
                                                    {
                                                        if (value)
                                                            item.setValue(value);
                                                    }}
                                                    style={{ width: NSBox.width.triple }}
                                                />
                                            )
                                        }
                                    ]
                                }
                            }
                        }
                    }
                }}
            >
                <NSColumn>
                    <NSRow>
                        <NSBoxString
                            ref={this.NSBoxString_Name}
                            required
                            title="Name"
                            style={{ width: NSBox.width.double }}
                            info={{ link: "https://namirasoft.com/inference/console-guide/condition-group/#field-name" }}
                        />
                    </NSRow>
                    <NSRow>
                        <NSBoxBoolean
                            ref={this.NSBoxBoolean_Not}
                            headerTitle="Not"
                            title="Invert the group result"
                            required={false}
                            info={{ link: "https://namirasoft.com/inference/console-guide/condition-group/#field-not" }}
                        />
                        <NSBoxEnum<ConditionGroupOperator>
                            ref={this.NSBoxEnum_Operator}
                            title="Operator"
                            required
                            multiple={false}
                            getEnumObject={() => ConditionGroupOperator}
                            info={{ link: "https://namirasoft.com/inference/console-guide/condition-group/#field-operator" }}
                        />
                    </NSRow>
                    <NSRow>
                        <NSBoxString
                            ref={this.NSBoxString_LogGroupId}
                            required={false}
                            title="Log Group ID"
                            style={{ width: NSBox.width.double }}
                            info={{ link: "https://namirasoft.com/inference/console-guide/condition-group/#field-log-group" }}
                        />
                    </NSRow>
                    <NSBoxTextArea
                        ref={this.NSBoxTextArea_Description}
                        title="Description"
                        required={false}
                        style={{ width: NSBox.width.double }}
                        info={{ link: "https://namirasoft.com/inference/console-guide/condition-group/#field-description" }}
                    />
                </NSColumn>
            </InferenceLayout>
        );
    }
}